/** 人脉订单列表：可配置列（排序 / 省略 / 显示，对齐设计稿「自定义列表」） */

export const NETWORK_ORDER_LIST_STORAGE_KEY = 'admin.networkOrder.listColumns.v3'

/** @type {{ key: string, label: string, defaultOn?: boolean, locked?: boolean, minWidth?: string, noEllipsis?: boolean, ellipsisDefault?: boolean }[]} */
export const NETWORK_ORDER_COLUMN_DEFS = [
  { key: 'seq', label: '序号', defaultOn: true, locked: true, minWidth: '56px', noEllipsis: true },
  { key: 'stageLabel', label: '阶段状态', defaultOn: true, minWidth: '100px' },
  { key: 'auditStatusLabel', label: '审核状态', defaultOn: true, minWidth: '88px' },
  { key: 'applicantInfo', label: '申请人信息', defaultOn: true, minWidth: '140px', ellipsisDefault: true },
  { key: 'applicantCompany', label: '申请人所属组织', defaultOn: true, minWidth: '160px', ellipsisDefault: true },
  { key: 'applyBusiness', label: '申请业务', defaultOn: true, minWidth: '100px', ellipsisDefault: true },
  { key: 'applicantTitle', label: '申请人职务', defaultOn: true, minWidth: '100px', ellipsisDefault: true },
  { key: 'targetCompany', label: '申请查看组织', defaultOn: true, minWidth: '160px', ellipsisDefault: true },
  { key: 'targetName', label: '申请查看人员', defaultOn: true, minWidth: '100px', ellipsisDefault: true },
  { key: 'targetTitle', label: '查看人员职务', defaultOn: true, minWidth: '100px', ellipsisDefault: true },
  { key: 'applyReason', label: '需求描述', defaultOn: true, minWidth: '160px', ellipsisDefault: true },
  { key: 'chatParticipants', label: '群聊管理员', defaultOn: true, minWidth: '140px', ellipsisDefault: true },
  { key: 'createdAt', label: '创建时间', defaultOn: true, minWidth: '150px' },
  { key: 'orderNo', label: '申请单号', defaultOn: false, minWidth: '140px', ellipsisDefault: true },
  { key: 'amountCent', label: '解锁金额', defaultOn: false, minWidth: '88px' },
  { key: 'reviewer', label: '审核人', defaultOn: false, minWidth: '80px' },
  { key: 'reviewedAt', label: '审核时间', defaultOn: false, minWidth: '150px' },
  { key: 'actions', label: '操作', defaultOn: true, locked: true, minWidth: '72px', noEllipsis: true },
]

const DEF_MAP = Object.fromEntries(NETWORK_ORDER_COLUMN_DEFS.map((c) => [c.key, c]))

export function defaultColumnConfig() {
  return NETWORK_ORDER_COLUMN_DEFS.map((c, i) => ({
    key: c.key,
    sort: i + 1,
    visible: c.defaultOn !== false,
    ellipsis: c.noEllipsis ? false : c.ellipsisDefault !== false,
  }))
}

function migrateFromV1Keys(keys) {
  const allowed = new Set(NETWORK_ORDER_COLUMN_DEFS.map((c) => c.key))
  const ordered = []
  keys.forEach((k) => {
    if (allowed.has(k) && !ordered.includes(k)) ordered.push(k)
  })
  NETWORK_ORDER_COLUMN_DEFS.filter((c) => c.locked).forEach((c) => {
    if (!ordered.includes(c.key)) ordered.push(c.key)
  })
  const defaults = defaultColumnConfig()
  const defByKey = Object.fromEntries(defaults.map((x) => [x.key, x]))
  return ordered.map((key, i) => ({
    key,
    sort: i + 1,
    visible: true,
    ellipsis: defByKey[key] ? defByKey[key].ellipsis : true,
  }))
}

export function loadColumnConfig() {
  try {
    const raw = localStorage.getItem(NETWORK_ORDER_LIST_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed?.items) && parsed.items.length) {
        return normalizeColumnConfig(parsed.items)
      }
    }
    const legacy = localStorage.getItem('admin.networkOrder.listColumns.v1')
    if (legacy) {
      const keys = JSON.parse(legacy)
      if (Array.isArray(keys) && keys.length) {
        return normalizeColumnConfig(migrateFromV1Keys(keys))
      }
    }
  } catch {
    /* ignore */
  }
  return defaultColumnConfig()
}

export function normalizeColumnConfig(items) {
  const allowed = new Set(NETWORK_ORDER_COLUMN_DEFS.map((c) => c.key))
  const out = []
  const seen = new Set()
  ;(items || []).forEach((it) => {
    const key = it && it.key ? String(it.key) : ''
    if (!allowed.has(key) || seen.has(key)) return
    seen.add(key)
    const def = DEF_MAP[key] || {}
    out.push({
      key,
      sort: Number(it.sort) > 0 ? Number(it.sort) : out.length + 1,
      visible: def.locked ? true : it.visible !== false,
      ellipsis: def.noEllipsis ? false : it.ellipsis !== false,
    })
  })
  NETWORK_ORDER_COLUMN_DEFS.forEach((c) => {
    if (!seen.has(c.key)) {
      out.push({
        key: c.key,
        sort: out.length + 1,
        visible: c.defaultOn !== false,
        ellipsis: c.noEllipsis ? false : c.ellipsisDefault !== false,
      })
    }
  })
  out.sort((a, b) => a.sort - b.sort || a.key.localeCompare(b.key))
  out.forEach((it, i) => {
    it.sort = i + 1
  })
  return out
}

export function saveColumnConfig(items) {
  const normalized = normalizeColumnConfig(items)
  localStorage.setItem(
    NETWORK_ORDER_LIST_STORAGE_KEY,
    JSON.stringify({ items: normalized }),
  )
  return normalized
}

/** @deprecated use loadColumnConfig */
export function loadVisibleColumnKeys() {
  return loadColumnConfig()
    .filter((c) => c.visible)
    .map((c) => c.key)
}

/** @deprecated use saveColumnConfig */
export function saveVisibleColumnKeys(keys) {
  const set = new Set(keys || [])
  const items = defaultColumnConfig().map((c, i) => ({
    ...c,
    visible: set.has(c.key) || DEF_MAP[c.key]?.locked,
    sort: i + 1,
  }))
  return saveColumnConfig(items).filter((c) => c.visible).map((c) => c.key)
}

export function defaultVisibleColumnKeys() {
  return defaultColumnConfig()
    .filter((c) => c.visible)
    .map((c) => c.key)
}

export function visibleSortedColumnDefs(config) {
  const cfg = config || loadColumnConfig()
  const map = Object.fromEntries(cfg.map((c) => [c.key, c]))
  return NETWORK_ORDER_COLUMN_DEFS.filter((d) => map[d.key]?.visible)
    .sort((a, b) => (map[a.key]?.sort || 0) - (map[b.key]?.sort || 0))
    .map((d) => ({ ...d, ellipsis: map[d.key]?.ellipsis !== false }))
}

export function formatAmountCent(cent) {
  const c = cent == null ? 0 : Number(cent)
  if (!c) return '免费'
  return `¥${(c / 100).toFixed(2)}`
}

export function truncateText(text, max = 28) {
  const s = text == null ? '' : String(text).trim()
  if (s.length <= max) return s
  return `${s.slice(0, max)}…`
}
